"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserSubscriptionFeature } from "@/types/userSubscription.types";

interface SubscriptionFeaturePickerProps {
  features: UserSubscriptionFeature[];
  loading: boolean;
  selectedFeatureIds: string[];
  // Features already recorded on the appointment when the editor opened
  initialFeatureIds: string[];
  // Of those, the ones recorded as extra (beyond the subscription balance)
  initialExtraFeatureIds?: string[];
  onToggle: (featureId: string) => void;
  disabled?: boolean;
}

// Every feature can be selected. A counted feature with no balance left is
// recorded as extra (the admin adjusts the amount); Yes/No features never are.
// A feature already on this appointment keeps whatever it was saved as.
export function isExtraFeature(
  feature: UserSubscriptionFeature,
  initialFeatureIds: string[],
  initialExtraFeatureIds: string[],
): boolean {
  if (initialFeatureIds.includes(feature.id)) {
    return initialExtraFeatureIds.includes(feature.id);
  }
  if (feature.type === "Boolean") return false;
  return Number(feature.value) <= 0;
}

function isNumericValue(value: UserSubscriptionFeature["value"]): boolean {
  if (typeof value === "number") return true;
  return typeof value === "string" && value !== "" && !isNaN(Number(value));
}

export function SubscriptionFeaturePicker({
  features,
  loading,
  selectedFeatureIds,
  initialFeatureIds,
  initialExtraFeatureIds = [],
  onToggle,
  disabled = false,
}: SubscriptionFeaturePickerProps) {
  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading features...
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No features available
      </div>
    );
  }

  const selectedExtraNames = features
    .filter(
      (feature) =>
        selectedFeatureIds.includes(feature.id) &&
        isExtraFeature(feature, initialFeatureIds, initialExtraFeatureIds),
    )
    .map((feature) => feature.name);

  return (
    <>
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {features.map((feature) => {
          const isSelected = selectedFeatureIds.includes(feature.id);
          const isExtra = isExtraFeature(
            feature,
            initialFeatureIds,
            initialExtraFeatureIds,
          );
          const isUsedHere = initialFeatureIds.includes(feature.id);
          const isBoolean = feature.type === "Boolean";

          let valueLabel = (
            <span>Type: {feature.type}</span>
          );
          if (isBoolean) {
            valueLabel = (
              <span
                className={feature.value ? "text-green-600" : "text-gray-500"}
              >
                {feature.value ? "Enabled" : "Disabled"}
              </span>
            );
          } else if (isNumericValue(feature.value)) {
            valueLabel = <span>Balance: {feature.value}</span>;
          }

          return (
            <div
              key={feature.id}
              className="flex items-center justify-between p-3 border rounded hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  id={`feature-${feature.id}`}
                  checked={isSelected}
                  disabled={disabled}
                  onCheckedChange={() => onToggle(feature.id)}
                />
                <div className="flex-1">
                  <Label
                    htmlFor={`feature-${feature.id}`}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {feature.name}
                  </Label>
                  {isExtra && (
                    <Badge
                      variant="outline"
                      className="ml-2 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
                    >
                      Extra
                    </Badge>
                  )}
                  {isUsedHere && (
                    <Badge variant="secondary" className="ml-2">
                      Used on this appointment
                    </Badge>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    {valueLabel}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedFeatureIds.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {selectedFeatureIds.length} feature(s) selected
        </p>
      )}

      {selectedExtraNames.length > 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
          ⚠ {selectedExtraNames.join(", ")}{" "}
          {selectedExtraNames.length === 1 ? "is" : "are"} beyond the
          subscription balance and will be recorded as Extra. The admin will
          adjust the amount.
        </p>
      )}
    </>
  );
}
